// A governing document before a meeting (ADR 0073, amending ADRs 0068 and
// 0069): the change list, the redline and the clean proposed text in the
// packet and the board book, and at the meeting each section's Current,
// Proposed and As amended text, with the sections the board leaves out
// staying as in effect.
//
// Built from two sources that must read the same: the packet's frozen block
// (BoardBookManual) and the record as it stands (the version in effect, the
// draft as it stood when the meeting was called to order, the draft now).

import { manualRedline, sectionHeading, wordDiff, type BoardBookManual, type DiffOp, type ManualKind, type RedlineSectionLike, type SectionKind } from './governance-manuals'

// The only words these views show (owner, 2026-10-28: labels, no helper text).
export const DOC_REVIEW_LABELS = {
  current: 'Current',
  proposed: 'Proposed',
  amended: 'As amended',
  new: 'New',
  changed: 'Changed',
  added: 'Added',
  removed: 'Removed',
  showAll: 'Show all',
  staysInEffect: 'Stays as in effect',
  forInformation: 'For information',
} as const

export type DocChange = 'unchanged' | 'changed' | 'added' | 'removed'
export const DOC_CHANGE_LABELS: Record<Exclude<DocChange, 'unchanged'>, string> = { changed: DOC_REVIEW_LABELS.changed, added: DOC_REVIEW_LABELS.added, removed: DOC_REVIEW_LABELS.removed }

export type DocText = { title: string; body: string }

export type DocReviewSection = {
  key: string
  parentKey: string | null
  kind: SectionKind
  ref: string | null
  // "Art. III §9", "FIN-002", "Ch. 2".
  name: string
  // The heading as it reads in the text that would be adopted.
  heading: string
  // Against the version in effect, with any amendment at the meeting.
  change: DocChange
  current: DocText | null
  proposed: DocText | null
  // Only when the board amended it at the meeting.
  amended: DocText | null
  // Left out of the motion: stays as in effect.
  leftOut: boolean
  // Current to what would be adopted (Proposed, or As amended).
  redline: { title: DiffOp[]; body: DiffOp[] }
}

export type ChangeListEntry = { key: string; name: string; title: string; change: Exclude<DocChange, 'unchanged'>; amended: boolean; leftOut: boolean }

export type DocReview = {
  // No version in effect: the text only, labeled New.
  isNew: boolean
  basedOnNumber: number | null
  amended: boolean
  sections: DocReviewSection[]
  changeList: ChangeListEntry[]
}

// --------------------------------------------------------------- names

const roman = (ref: string | null) => (ref ?? '').replace(/^article\s+/i, '').trim()
const sectionNumber = (ref: string | null) => (ref ?? '').replace(/^(section|sec\.)\s+/i, '').trim()

// A section's short name for the change list: its id, or where it stands.
export function sectionShortName(s: { key: string; kind: SectionKind; ref: string | null; title: string }, parent?: { kind: SectionKind; ref: string | null } | null): string {
  if (s.kind === 'article') return s.ref ? `Art. ${roman(s.ref)}` : s.title || s.key
  if (s.kind === 'section') {
    const n = sectionNumber(s.ref)
    const here = n ? `§${n}` : s.title || s.key
    return parent && parent.kind === 'article' && parent.ref ? `Art. ${roman(parent.ref)} ${here}` : here
  }
  if (s.kind === 'chapter') return s.ref ? `Ch. ${s.ref}` : s.title || s.key
  if (s.ref) return s.ref
  if (s.title) return s.title
  return s.key === 'preamble' ? 'Preamble' : s.key
}

// "Art. III §9 changed · FIN-002 changed · GOV-015 added · RISK-005 removed".
export function changeListLine(entries: Pick<ChangeListEntry, 'name' | 'change'>[]): string {
  return entries.map((e) => `${e.name} ${DOC_CHANGE_LABELS[e.change].toLowerCase()}`).join(' · ')
}

// ------------------------------------------------------------- building

type Row = { key: string; parentKey: string | null; kind: SectionKind; ref: string | null; current: DocText | null; proposed: DocText | null }

const text = (s: { title: string; body: string }): DocText => ({ title: s.title, body: s.body })
const same = (a: DocText | null, b: DocText | null) => (a === null ? b === null : b !== null && a.title === b.title && a.body === b.body)
const ins = (t: string): DiffOp[] => (t ? [{ op: 'ins', text: t }] : [])
const del = (t: string): DiffOp[] => (t ? [{ op: 'del', text: t }] : [])
const eq = (t: string): DiffOp[] => (t ? [{ op: 'eq', text: t }] : [])

function review(rows: Row[], opts: { isNew: boolean; basedOnNumber: number | null; excludedKeys: string[]; amended?: Map<string, DocText> }): DocReview {
  const excluded = new Set(opts.excludedKeys)
  const byKey = new Map(rows.map((r) => [r.key, r]))
  const sections: DocReviewSection[] = rows.map((r) => {
    const amendedText = r.proposed && opts.amended?.get(r.key)
    const amended = amendedText && !same(amendedText, r.proposed) ? amendedText : null
    const leftOut = excluded.has(r.key)
    const voted = amended ?? r.proposed
    const change: DocChange = opts.isNew ? 'added' : !r.current ? 'added' : !voted ? 'removed' : same(r.current, voted) ? 'unchanged' : 'changed'
    // What would be adopted: left out, the text in effect stays.
    const result = leftOut ? r.current : voted
    const redline = opts.isNew
      ? { title: eq(result?.title ?? ''), body: eq(result?.body ?? '') }
      : !r.current
        ? { title: ins(result?.title ?? ''), body: ins(result?.body ?? '') }
        : !result
          ? { title: del(r.current.title), body: del(r.current.body) }
          : { title: wordDiff(r.current.title, result.title), body: wordDiff(r.current.body, result.body) }
    const shown = voted ?? r.current ?? { title: '', body: '' }
    const parentRow = r.parentKey ? byKey.get(r.parentKey) : null
    const parent = parentRow ? { kind: parentRow.kind, ref: parentRow.ref } : null
    return {
      key: r.key,
      parentKey: r.parentKey,
      kind: r.kind,
      ref: r.ref,
      name: sectionShortName({ key: r.key, kind: r.kind, ref: r.ref, title: shown.title }, parent),
      heading: sectionHeading({ kind: r.kind, ref: r.ref, title: shown.title }),
      change,
      current: opts.isNew ? null : r.current,
      proposed: r.proposed,
      amended,
      leftOut,
      redline,
    }
  })
  const changeList: ChangeListEntry[] = opts.isNew
    ? []
    : sections.filter((s) => s.change !== 'unchanged').map((s) => ({ key: s.key, name: s.name, title: (s.amended ?? s.proposed ?? s.current)?.title ?? '', change: s.change as ChangeListEntry['change'], amended: !!s.amended, leftOut: s.leftOut }))
  return { isNew: opts.isNew, basedOnNumber: opts.isNew ? null : opts.basedOnNumber, amended: sections.some((s) => !!s.amended), sections, changeList }
}

type Sec = RedlineSectionLike & { parentKey?: string | null }

// From the record: the version in effect (null or empty: a new document),
// the text proposed, and the text as amended at the meeting (by key; only
// sections the board changed there).
export function buildDocReview(input: { base: Sec[] | null; proposed: Sec[]; amended?: Map<string, DocText>; excludedKeys?: string[]; basedOnNumber: number | null }): DocReview {
  const isNew = !input.base || input.base.length === 0
  const base = new Map((input.base ?? []).map((s) => [s.key, s]))
  const proposed = new Map(input.proposed.map((s) => [s.key, s]))
  const order = manualRedline(isNew ? null : input.base, input.proposed).entries
  const rows: Row[] = order.map((e) => {
    const b = base.get(e.key) ?? null
    const p = proposed.get(e.key) ?? null
    const s = (p ?? b)!
    return { key: e.key, parentKey: s.parentKey ?? null, kind: s.kind, ref: s.ref, current: b ? text(b) : null, proposed: p ? text(p) : null }
  })
  return review(rows, { isNew, basedOnNumber: input.basedOnNumber, excludedKeys: input.excludedKeys ?? [], amended: input.amended })
}

const side = (ops: DiffOp[], keep: 'del' | 'ins') => ops.filter((o) => o.op === 'eq' || o.op === keep).map((o) => o.text).join('')

// From a packet's frozen block. The redline there lists only what changed;
// a removed section names the section before it (`after`), or follows the
// entry before it in a packet frozen before that was recorded.
export function docReviewFromBlock(block: Pick<BoardBookManual, 'sections' | 'redline' | 'excludedKeys' | 'basedOnNumber'>): DocReview {
  const isNew = !block.redline
  const entries = new Map((block.redline ?? []).map((e) => [e.key, e]))
  const rows: Row[] = []
  const removedAfter = new Map<string | null, NonNullable<BoardBookManual['redline']>>()
  let prev: string | null = null
  for (const e of block.redline ?? []) {
    if (e.change === 'removed') {
      const at = e.after !== undefined ? e.after : prev
      removedAfter.set(at, [...(removedAfter.get(at) ?? []), e])
    } else prev = e.key
  }
  const removedRow = (e: NonNullable<BoardBookManual['redline']>[number]): Row => ({ key: e.key, parentKey: e.parentKey ?? null, kind: e.kind, ref: e.ref, current: { title: side(e.title, 'del'), body: side(e.body, 'del') }, proposed: null })
  for (const e of removedAfter.get(null) ?? []) rows.push(removedRow(e))
  for (const s of block.sections) {
    const e = entries.get(s.key)
    const proposed = text(s)
    const current = isNew ? null : !e ? proposed : e.change === 'new' ? null : { title: side(e.title, 'del'), body: side(e.body, 'del') }
    rows.push({ key: s.key, parentKey: s.parentKey, kind: s.kind, ref: s.ref, current, proposed })
    for (const r of removedAfter.get(s.key) ?? []) rows.push(removedRow(r))
  }
  // Removed entries whose place is no longer in the text go at the end.
  const placed = new Set(rows.map((r) => r.key))
  for (const e of block.redline ?? []) if (e.change === 'removed' && !placed.has(e.key)) rows.push(removedRow(e))
  return review(rows, { isNew, basedOnNumber: block.basedOnNumber, excludedKeys: block.excludedKeys })
}

// Whether two reviews say the same thing: the same sections, changes, text
// in effect and text proposed (the bylaws' notice of intent: the packet
// that gave notice against what is voted on).
export function sameDocReview(a: DocReview, b: DocReview): boolean {
  const key = (r: DocReview) => JSON.stringify([r.isNew, r.sections.map((s) => [s.key, s.change, s.current, s.amended ?? s.proposed])])
  return key(a) === key(b)
}

// The sections shown by default at the meeting: those that change, those
// amended, and those left out.
export const changedSections = (r: DocReview) => (r.isNew ? r.sections : r.sections.filter((s) => s.change !== 'unchanged' || s.leftOut || !!s.amended))

// The minutes' record of a document adopted (or recommended): by reference
// to the version and the sections it changed; what the board amended there
// and what it left out.
// changeList: the version as adopted against the version in effect.
export function docMinutesLines(input: { title: string; number: number; hash: string | null; isNew: boolean; changeList: Pick<ChangeListEntry, 'name' | 'change'>[]; amended: string[]; leftOut: string[] }): string[] {
  const line = `${input.title}, version ${input.number}${input.hash ? ` (SHA-256 ${input.hash})` : ''}.`
  const lines = [input.isNew ? `${line} ${DOC_REVIEW_LABELS.new}.` : line]
  if (!input.isNew)
    for (const change of ['changed', 'added', 'removed'] as const) {
      const list = input.changeList.filter((e) => e.change === change).map((e) => e.name)
      if (list.length > 0) lines.push(`${DOC_CHANGE_LABELS[change]}: ${list.join(', ')}.`)
    }
  if (input.amended.length > 0) lines.push(`${DOC_REVIEW_LABELS.amended}: ${input.amended.join(', ')}.`)
  if (input.leftOut.length > 0) lines.push(`${DOC_REVIEW_LABELS.staysInEffect}: ${input.leftOut.join(', ')}.`)
  return lines
}

// A document on a meeting's agenda, as Hub's conductor and myBRE show it.
export type MeetingDocumentView = {
  agendaItemId: string
  motionId: string | null
  motionStatus: string | null
  effect: 'recommends' | 'adopts' | 'informs'
  versionId: string
  manualTitle: string
  kind: ManualKind
  number: number
  // As the latest published packet froze it.
  packet: { version: number; review: DocReview } | null
  // The record now: Proposed as the meeting was called to order, As amended since.
  live: DocReview
  // Staff only: the motion is before the meeting, in session.
  canAmend: boolean
  // Staff only: an amendment to it is pending.
  amendmentPending: boolean
}
